// Slider de "barra rellena" para el modo "Video con influencer" (V2).
//
// Layout (ADR-528/529): el TÍTULO va FUERA de la barra — una fila arriba con
// el label (izquierda) y el valor (derecha) — y la barra de abajo es solo el
// slider: un rectángulo con esquinas redondeadas que se llena de azul sólido
// (brand-primary) hasta la posición elegida. Drag/tap con Pointer Events.
// Lo usan "Velocidad de habla" y "Duración" de la sección Voz y guion.

import React from 'react';

// Parametría de velocidad de habla — misma que el modo diálogo del Director
// de Estudio (0.5×–1.5×, paso 0.05, default 1.1×). Si se cambia acá,
// actualizar también el inline del editor (ver ADR-527).
export const SPEECH_SPEED_MIN = 0.5;
export const SPEECH_SPEED_MAX = 1.5;
export const SPEECH_SPEED_STEP = 0.05;
export const SPEECH_SPEED_DEFAULT = 1.1;

export interface FillBarSliderV2Props {
  /** Label arriba de la barra (ej. "Velocidad de habla"). */
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  /** Render del valor a la derecha del label (ej. `x 1.10`, `18s`). */
  formatValue: (v: number) => string;
}

export const FillBarSliderV2: React.FC<FillBarSliderV2Props> = ({
  label,
  min,
  max,
  step,
  value,
  onChange,
  formatValue,
}) => {
  const stepCount = Math.max(1, Math.round((max - min) / step));

  const calcVal = (clientX: number, rect: DOMRect) => {
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const stepIdx = Math.round(ratio * stepCount);
    return Math.round((min + stepIdx * step) * 100) / 100;
  };

  const clamped = Math.min(max, Math.max(min, value));
  const fillPct = ((clamped - min) / (max - min)) * 100;

  return (
    <div>
      {/* Título + valor — FUERA de la barra */}
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
          {label}
        </p>
        <span className="text-[13px] font-semibold text-brand-primary tabular-nums whitespace-nowrap">
          {formatValue(clamped)}
        </span>
      </div>

      {/* Barra: rectángulo redondeado que se llena de azul hasta el valor */}
      <div
        className="relative h-10 rounded-xl bg-white border border-neutral-200 overflow-hidden select-none cursor-pointer"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => {
          const el = e.currentTarget;
          el.setPointerCapture(e.pointerId);
          const rect = el.getBoundingClientRect();
          onChange(calcVal(e.clientX, rect));
          const onMove = (ev: PointerEvent) =>
            onChange(calcVal(ev.clientX, rect));
          const onUp = () => {
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerup', onUp);
            el.removeEventListener('pointercancel', onUp);
          };
          el.addEventListener('pointermove', onMove);
          el.addEventListener('pointerup', onUp);
          el.addEventListener('pointercancel', onUp);
        }}
      >
        <div
          className="absolute inset-y-0 left-0 bg-brand-primary pointer-events-none"
          style={{ width: `${fillPct}%` }}
        />
      </div>
    </div>
  );
};
